import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { getPortalLink } from "../../utils/portalLinks";

const PortalAccessContext = createContext(null);

const POLL_INTERVAL_MS = 2000;
const REQUEST_TIMEOUT_MS = 3500;
const MAX_WAIT_MS = 45000;

const getPortalLabel = (role) => {
  if (role === "admin") {
    return "Admin Portal";
  }

  if (role === "applicant") {
    return "Applicant Access";
  }

  if (role === "student") {
    return "Student Portal";
  }

  if (role === "parent") {
    return "Parent Portal";
  }

  return "Educator Portal";
};

const wait = (ms, signal) =>
  new Promise((resolve, reject) => {
    let isSettled = false;
    const timeoutId = window.setTimeout(() => {
      isSettled = true;
      signal?.removeEventListener("abort", handleAbort);
      resolve();
    }, ms);

    if (!signal) {
      return;
    }

    function handleAbort() {
      if (isSettled) {
        return;
      }

      isSettled = true;
      window.clearTimeout(timeoutId);
      reject(new DOMException("Portal check aborted", "AbortError"));
    }

    if (signal.aborted) {
      handleAbort();
      return;
    }

    signal.addEventListener("abort", handleAbort, { once: true });
  });

const probePortal = async (url, signal) => {
  const controller = new AbortController();
  const probeUrl = new URL(url);
  const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  const handleAbort = () => controller.abort();

  if (signal?.aborted) {
    throw new DOMException("Portal check aborted", "AbortError");
  }

  signal?.addEventListener("abort", handleAbort, { once: true });
  probeUrl.searchParams.set("_portalProbe", String(Date.now()));

  try {
    await fetch(probeUrl.toString(), {
      method: "GET",
      mode: "no-cors",
      cache: "no-store",
      signal: controller.signal,
    });

    return true;
  } catch (error) {
    if (signal?.aborted) {
      throw error;
    }

    return false;
  } finally {
    window.clearTimeout(timeoutId);
    signal?.removeEventListener("abort", handleAbort);
  }
};

function PortalAccessOverlay({ state, onClose, onRetry, onOpenNow }) {
  if (!state.isOpen) {
    return null;
  }

  const isBusy = state.status === "checking" || state.status === "waiting" || state.status === "ready";

  return (
    <div className="portal-access-overlay" role="presentation">
      <div
        className={`portal-access-card portal-access-card-${state.status}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="portal-access-title"
        aria-describedby="portal-access-message"
      >
        <div className="portal-access-badge">
          {state.label}
        </div>

        {isBusy ? <div className="portal-access-spinner" aria-hidden="true" /> : null}

        <h2 id="portal-access-title" className="portal-access-title">
          {state.title}
        </h2>

        <p id="portal-access-message" className="portal-access-message">
          {state.message}
        </p>

        {state.detail ? (
          <p className="portal-access-detail">
            {state.detail}
          </p>
        ) : null}

        <div className="portal-access-actions">
          {state.status === "timeout" ? (
            <>
              <button type="button" className="portal-access-button portal-access-button-primary" onClick={onRetry}>
                Retry
              </button>
              <button type="button" className="portal-access-button portal-access-button-secondary" onClick={onOpenNow}>
                Open Anyway
              </button>
              <button type="button" className="portal-access-button portal-access-button-ghost" onClick={onClose}>
                Stay Here
              </button>
            </>
          ) : (
            <button type="button" className="portal-access-button portal-access-button-ghost" onClick={onClose}>
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function PortalAccessProvider({ children }) {
  const [state, setState] = useState({
    isOpen: false,
    status: "idle",
    role: "",
    label: "",
    targetUrl: "",
    title: "",
    message: "",
    detail: "",
  });

  const requestRef = useRef({
    controller: null,
    redirectTimerId: null,
  });

  const clearActiveRequest = useCallback(() => {
    if (requestRef.current.controller) {
      requestRef.current.controller.abort();
      requestRef.current.controller = null;
    }

    if (requestRef.current.redirectTimerId) {
      window.clearTimeout(requestRef.current.redirectTimerId);
      requestRef.current.redirectTimerId = null;
    }
  }, []);

  const closeOverlay = useCallback(() => {
    clearActiveRequest();
    setState((current) => ({
      ...current,
      isOpen: false,
      status: "idle",
    }));
  }, [clearActiveRequest]);

  const openPortal = useCallback(async (role) => {
    const targetUrl = getPortalLink(role);
    const label = getPortalLabel(role);
    const controller = new AbortController();
    const startedAt = Date.now();

    clearActiveRequest();
    requestRef.current.controller = controller;

    setState({
      isOpen: true,
      status: "checking",
      role,
      label,
      targetUrl,
      title: `Checking ${label}`,
      message: "Please wait while we confirm the portal is ready.",
      detail: "If the portal is still starting, this page will keep checking and open it automatically.",
    });

    try {
      let available = await probePortal(targetUrl, controller.signal);

      while (!available && !controller.signal.aborted && Date.now() - startedAt < MAX_WAIT_MS) {
        setState({
          isOpen: true,
          status: "waiting",
          role,
          label,
          targetUrl,
          title: `${label} is still starting`,
          message: "The portal is not reachable yet.",
          detail: "Keep this page open. You will be redirected automatically as soon as the portal is ready.",
        });

        await wait(POLL_INTERVAL_MS, controller.signal);
        available = await probePortal(targetUrl, controller.signal);
      }

      if (controller.signal.aborted) {
        return;
      }

      if (available) {
        setState({
          isOpen: true,
          status: "ready",
          role,
          label,
          targetUrl,
          title: `${label} is ready`,
          message: "Opening the portal now.",
          detail: "You will be redirected in a moment.",
        });

        requestRef.current.redirectTimerId = window.setTimeout(() => {
          window.location.assign(targetUrl);
        }, 450);

        return;
      }

      setState({
        isOpen: true,
        status: "timeout",
        role,
        label,
        targetUrl,
        title: `${label} is not ready yet`,
        message: "We could not reach the portal within the expected time.",
        detail: "The portal may still be booting up. Retry, or open it anyway if you want to try the destination directly.",
      });
    } catch (error) {
      if (error?.name !== "AbortError") {
        setState({
          isOpen: true,
          status: "timeout",
          role,
          label,
          targetUrl,
          title: `${label} is not ready yet`,
          message: "We could not reach the portal right now.",
          detail: "Retry in a moment, or open it directly if the portal server has already started.",
        });
      }
    }
  }, [clearActiveRequest]);

  useEffect(() => {
    if (!state.isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [state.isOpen]);

  useEffect(() => () => clearActiveRequest(), [clearActiveRequest]);

  return (
    <PortalAccessContext.Provider value={{ openPortal }}>
      {children}
      <PortalAccessOverlay
        state={state}
        onClose={closeOverlay}
        onRetry={() => openPortal(state.role)}
        onOpenNow={() => window.location.assign(state.targetUrl)}
      />
    </PortalAccessContext.Provider>
  );
}

export function usePortalAccess() {
  const context = useContext(PortalAccessContext);

  if (!context) {
    throw new Error("usePortalAccess must be used within PortalAccessProvider");
  }

  return context;
}
